import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { PageHeader } from '@components/molecules/PageHeader';
import { StatsCard } from '@components/molecules/StatsCard';
import { SearchInput } from '@components/molecules/SearchInput';
import GeofenceTable from '@components/organisms/GeofenceTable';
import GeofenceMap from '@components/organisms/GeofenceMap';
import GeofenceFormModal from '@components/organisms/GeofenceFormModal';
import GeofenceInfoCard from '@components/organisms/GeofenceInfoCard';
import {
  useGeofences,
  useCreateGeofence,
  useUpdateGeofence,
  useSetGeofenceActive,
} from '@hooks/api/geofence';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useWarehouses } from '@hooks/api/warehouse/useWarehouses';
import {
  Geofence,
  GeofenceCreateInput,
  GeofenceShapeType,
  GeofenceUpdateInput,
} from '@models/geofence.model';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PinDropIcon from '@mui/icons-material/PinDrop';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import RoomIcon from '@mui/icons-material/Room';
import PolylineIcon from '@mui/icons-material/Polyline';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

const ALL_OPTION_VALUE = '__all__';
const PAGE_SIZE = 10;

const SELECTION_STORAGE_KEY = 'geofences:lastViewedId';

const readStoredSelection = (): string | null => {
  try {
    return window.localStorage.getItem(SELECTION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const GeofencesPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const location = useLocation();
  // Selection priority: ?selected=<id> > localStorage > first row. The URL
  // param wins so deep-links from the project / warehouse modals always show
  // the requested geofence even if the user had a different one stored.
  //
  // The param is tracked reactively: React Router keeps this component mounted
  // when only the query string changes, so reading it once on mount would make
  // a second "Go to geofence" push silently do nothing.
  const selectedParam = useMemo(
    () => new URLSearchParams(location.search).get('selected'),
    [location.search],
  );
  // localStorage is only a fallback for the first paint — read once so it can
  // never override a later URL param.
  const initialStoredId = useMemo(() => readStoredSelection(), []);
  const initialSelectedId = selectedParam ?? initialStoredId;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [filterWarehouseId, setFilterWarehouseId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Geofence | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);
  // Once the initial id (URL or storage) is reconciled with the loaded list
  // we flip this so the auto-select-first effect can take over for
  // subsequent filter changes.
  const [hasAppliedInitialSelection, setHasAppliedInitialSelection] = useState(
    !initialSelectedId,
  );
  // Set right after creating a geofence — once it appears in the refetched
  // list we promote it to `selectedId`. Without this the auto-select-first
  // effect would race the refetch and fall back to the previous top row.
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);

  const { data: geofences = [], isLoading } = useGeofences();
  const { data: projects = [] } = useProjects();
  const { data: warehouses = [] } = useWarehouses();

  const createMutation = useCreateGeofence();
  const updateMutation = useUpdateGeofence();
  const toggleActiveMutation = useSetGeofenceActive();

  const totalCount = geofences.length;
  const activeCount = geofences.filter((g) => g.isActive).length;
  const circleCount = geofences.filter(
    (g) => g.shape.type === GeofenceShapeType.POINT_RADIUS,
  ).length;
  const polygonCount = geofences.filter(
    (g) => g.shape.type === GeofenceShapeType.POLYGON,
  ).length;

  // Filter — search text + project + warehouse
  const filteredGeofences = useMemo(() => {
    let result = geofences;

    if (filterProjectId) {
      result = result.filter((g) => g.projectId === filterProjectId);
    }
    if (filterWarehouseId) {
      result = result.filter((g) => g.warehouseId === filterWarehouseId);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (g) =>
          g.name.toLowerCase().includes(query) ||
          g.description?.toLowerCase().includes(query) ||
          g.category?.name.toLowerCase().includes(query) ||
          g.project?.name.toLowerCase().includes(query) ||
          g.warehouse?.name.toLowerCase().includes(query),
      );
    }
    return result;
  }, [geofences, filterProjectId, filterWarehouseId, searchQuery]);

  const totalFiltered = filteredGeofences.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));

  // Reset page if filters reduce results below current page
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedGeofences = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredGeofences.slice(start, start + PAGE_SIZE);
  }, [filteredGeofences, page]);

  // Map shows only the selected geofence to avoid visual clutter from nearby
  // default geofences. If nothing is selected the map stays empty (the user
  // clicks a row in the table to focus on a specific area).
  const mapGeofences = useMemo(() => {
    if (!selectedId) return [];
    const found = filteredGeofences.find((g) => g.geofenceId === selectedId);
    return found ? [found] : [];
  }, [selectedId, filteredGeofences]);

  // Apply the initial selection (URL param or localStorage) once the
  // geofences list arrives. If the requested id isn't visible (wrong org /
  // deleted) we fall through and let the auto-select-first effect pick the
  // top row.
  useEffect(() => {
    if (hasAppliedInitialSelection) return;
    if (geofences.length === 0) return;
    if (initialSelectedId) {
      const found = geofences.some((g) => g.geofenceId === initialSelectedId);
      if (found) setSelectedId(initialSelectedId);
    }
    setHasAppliedInitialSelection(true);
  }, [geofences, initialSelectedId, hasAppliedInitialSelection]);

  // Auto-select the first geofence when the list loads or filters change so
  // the map doesn't start empty. Skip while a pending create is in flight —
  // otherwise we'd flash to the old first row before the new one arrives.
  useEffect(() => {
    if (pendingSelectId) return;
    if (!hasAppliedInitialSelection) return;
    if (filteredGeofences.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const stillVisible = filteredGeofences.some((g) => g.geofenceId === selectedId);
    if (!stillVisible) {
      setSelectedId(filteredGeofences[0].geofenceId);
    }
  }, [filteredGeofences, selectedId, pendingSelectId, hasAppliedInitialSelection]);

  // Persist the current selection so the user lands on the same geofence
  // when returning to this page.
  useEffect(() => {
    if (!selectedId) return;
    try {
      window.localStorage.setItem(SELECTION_STORAGE_KEY, selectedId);
    } catch {
      // Storage may be disabled in private mode — silently ignore.
    }
  }, [selectedId]);

  // Promote a pending-created geofence to selected once the refetched list
  // contains it.
  useEffect(() => {
    if (!pendingSelectId) return;
    if (filteredGeofences.some((g) => g.geofenceId === pendingSelectId)) {
      setSelectedId(pendingSelectId);
      setPendingSelectId(null);
    }
  }, [filteredGeofences, pendingSelectId]);

  // Honour a `?selected=` that arrives *after* mount. React Router keeps this
  // page mounted when only the query string changes, so a second "Go to
  // geofence" would otherwise leave the previous selection in place.
  const appliedParamRef = useRef<string | null>(selectedParam);
  useEffect(() => {
    if (!selectedParam) return;
    if (appliedParamRef.current === selectedParam) return;
    appliedParamRef.current = selectedParam;
    setSelectedId(selectedParam);
  }, [selectedParam]);

  // Page the table to wherever the selected geofence actually is. Without this
  // a deep-link (or any selection outside page 1) showed the geofence on the
  // map while its row stayed invisible in the list.
  //
  // Keyed on selection + filters, not on `page`, so the user can still paginate
  // freely afterwards without being yanked back.
  const selectionPageKey = `${selectedId ?? ''}|${filterProjectId}|${filterWarehouseId}|${searchQuery}`;
  const pagedForSelectionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId) return;
    if (pagedForSelectionRef.current === selectionPageKey) return;

    const index = filteredGeofences.findIndex((g) => g.geofenceId === selectedId);
    // Not in the list yet (still loading) or filtered out — leave paging alone
    // and retry once the list settles.
    if (index === -1) return;

    pagedForSelectionRef.current = selectionPageKey;
    const targetPage = Math.floor(index / PAGE_SIZE) + 1;
    setPage((current) => (current === targetPage ? current : targetPage));
  }, [selectedId, selectionPageKey, filteredGeofences]);

  // Filter options
  const projectOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_OPTION_VALUE, label: 'All projects' },
      ...projects.map((p: any) => ({ value: p.projectId, label: p.name })),
    ],
    [projects],
  );

  const warehouseOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_OPTION_VALUE, label: 'All warehouses' },
      ...warehouses.map((w: any) => ({ value: w.warehouseId, label: w.name })),
    ],
    [warehouses],
  );

  const noProjects = projects.length === 0;
  const noWarehouses = warehouses.length === 0;

  const selectedProjectOption = useMemo(
    () =>
      filterProjectId
        ? projectOptions.find((o) => o.value === filterProjectId) || null
        : projectOptions[0] || null,
    [filterProjectId, projectOptions],
  );
  const selectedWarehouseOption = useMemo(
    () =>
      filterWarehouseId
        ? warehouseOptions.find((o) => o.value === filterWarehouseId) || null
        : warehouseOptions[0] || null,
    [filterWarehouseId, warehouseOptions],
  );

  // Handlers
  const handleProjectFilterChange = (
    opt: SingleValue<SelectOption> | MultiValue<SelectOption> | null,
  ) => {
    setPage(1);
    if (!opt || Array.isArray(opt) || !('value' in opt)) {
      // Cleared via the (x) button or unexpected shape
      setFilterProjectId('');
      return;
    }
    setFilterProjectId(opt.value === ALL_OPTION_VALUE ? '' : (opt.value as string));
  };
  const handleWarehouseFilterChange = (
    opt: SingleValue<SelectOption> | MultiValue<SelectOption> | null,
  ) => {
    setPage(1);
    if (!opt || Array.isArray(opt) || !('value' in opt)) {
      setFilterWarehouseId('');
      return;
    }
    setFilterWarehouseId(opt.value === ALL_OPTION_VALUE ? '' : (opt.value as string));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleOpenCreate = useCallback(() => {
    setEditing(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((geofence: Geofence) => {
    setEditing(geofence);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditing(null);
  }, []);

  const handleSubmit = useCallback(
    async (data: GeofenceCreateInput | GeofenceUpdateInput) => {
      try {
        if ('geofenceId' in data) {
          await updateMutation.mutateAsync(data);
          toast.success(tActionMessages.geofenceUpdated);
        } else {
          // After a successful create, surface the new row by clearing
          // filters/search and pre-selecting it. The list is sorted by
          // createdAt DESC so resetting to page 1 keeps it visible. We use
          // `pendingSelectId` instead of `setSelectedId` because the list
          // refetch is async — the pending effect promotes the id to
          // selected once the new row actually shows up.
          const created = await createMutation.mutateAsync(data);
          toast.success(tActionMessages.geofenceCreated);
          setSearchQuery('');
          setFilterProjectId('');
          setFilterWarehouseId('');
          setPage(1);
          setPendingSelectId(created.geofenceId);
        }
        handleCloseForm();
      } catch (err: any) {
        const message =
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          tActionMessages.failedToSaveGeofence;
        toast.error(message);
      }
    },
    [
      createMutation,
      updateMutation,
      handleCloseForm,
      tActionMessages.geofenceCreated,
      tActionMessages.geofenceUpdated,
      tActionMessages.failedToSaveGeofence,
    ],
  );

  const handleToggleActive = useCallback(
    async (geofence: Geofence) => {
      try {
        await toggleActiveMutation.mutateAsync({
          geofenceId: geofence.geofenceId,
          isActive: !geofence.isActive,
        });
        toast.success(geofence.isActive ? tActionMessages.geofenceDisabled : tActionMessages.geofenceEnabled);
      } catch (err: any) {
        toast.error(getErrorMessage(err, tActionMessages.failedToUpdateStatus));
      }
    },
    [toggleActiveMutation, tActionMessages.geofenceDisabled, tActionMessages.geofenceEnabled, tActionMessages.failedToUpdateStatus],
  );

  const isMutating = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-12">
        <Loading size="3xl" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Geofences"
        icon={<PinDropOutlinedIcon sx={{ fontSize: 28 }} />}
        actions={
          <>
            <Link to="/geofences/categories">
              <Button
                variant="secondary"
                leftIcon={<CategoryIcon sx={{ fontSize: 16 }} />}
              >
                Categories
              </Button>
            </Link>
            <Button
              variant="primary"
              leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleOpenCreate}
            >
              Add Geofence
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatsCard
          icon={<PinDropIcon className="text-gray-400" sx={{ fontSize: 24 }} />}
          value={totalCount}
          label="Total Geofences"
        />
        <StatsCard
          icon={<CheckCircleIcon className="text-green-500" sx={{ fontSize: 24 }} />}
          value={activeCount}
          label="Active"
        />
        <StatsCard
          icon={<RoomIcon className="text-blue-500" sx={{ fontSize: 24 }} />}
          value={circleCount}
          label="Circles"
        />
        <StatsCard
          icon={<PolylineIcon className="text-purple-500" sx={{ fontSize: 24 }} />}
          value={polygonCount}
          label="Polygons"
        />
      </div>

      {/* Filters: search + project + warehouse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search geofences..."
          className="w-full"
        />
        <ReactSelect
          options={projectOptions}
          value={noProjects ? null : selectedProjectOption}
          onChange={handleProjectFilterChange}
          placeholder={noProjects ? 'No projects available' : 'Filter by project'}
          isDisabled={noProjects}
        />
        <ReactSelect
          options={warehouseOptions}
          value={noWarehouses ? null : selectedWarehouseOption}
          onChange={handleWarehouseFilterChange}
          placeholder={noWarehouses ? 'No warehouses available' : 'Filter by warehouse'}
          isDisabled={noWarehouses}
        />
      </div>

      {/* Two-column layout: compact table on the left, info card + map on the
          right. The info card mirrors the row the user clicked so the full
          details stay visible without crowding the table. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="min-w-0">
          <GeofenceTable
            geofences={paginatedGeofences}
            selectedId={selectedId}
            onSelect={(g) => setSelectedId(g.geofenceId)}
            onEdit={handleOpenEdit}
            fillHeight={false}
            compact
            onToggleActive={handleToggleActive}
            page={page}
            pageSize={PAGE_SIZE}
            total={totalFiltered}
            onPageChange={setPage}
          />
        </div>
        <div className="flex flex-col gap-3 min-w-0">
          <GeofenceInfoCard geofence={mapGeofences[0] ?? null} />
          <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-[480px]">
            <GeofenceMap
              geofences={mapGeofences}
              selectedGeofenceId={selectedId}
              onSelectGeofence={(g) => setSelectedId(g.geofenceId)}
            />
          </div>
        </div>
      </div>

      {/* `key` forces a fresh mount whenever the user opens the modal for a
          different geofence (or for "Add new"). Without it, the modal stays
          mounted in the tree with stale internal state from the previous open
          — which is why the polygon/circle wouldn't render until you closed
          and reopened. */}
      <GeofenceFormModal
        key={isFormOpen ? `edit-${editing?.geofenceId ?? 'new'}` : 'closed'}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        geofence={editing}
        isLoading={isMutating}
      />
    </>
  );
};

export default GeofencesPage;
